import React from 'react';
import { Link } from 'react-router-dom';
import { Clock, MapPin, Building2, ExternalLink, ChevronRight, AlertTriangle, ShieldAlert } from 'lucide-react';
import { NormalizedAlert } from '../../types/alert';
import { SeverityBadge } from '../common/SeverityBadge';
import { formatDateSafe, formatRelativeTime, isAlertActive, getSeverityVisuals } from '../../utils/formatters';

interface AlertCardProps {
  alert: NormalizedAlert;
}

export const AlertCard: React.FC<AlertCardProps> = ({ alert }) => {
  const active = isAlertActive(alert.expires);
  const visuals = getSeverityVisuals(alert.severity);

  // Left accent border styling by severity
  const severityBorderLeft = {
    Extreme: 'border-l-red-600',
    Severe: 'border-l-orange-500',
    Moderate: 'border-l-amber-500',
    Minor: 'border-l-blue-500',
    Unknown: 'border-l-slate-400',
  }[alert.severity] || 'border-l-slate-400';

  return (
    <article
      className={`bg-white border border-slate-200/90 rounded-2xl shadow-xs hover:shadow-card-hover transition-all duration-200 hover:-translate-y-0.5 flex flex-col justify-between overflow-hidden border-l-4 ${severityBorderLeft}`}
      aria-labelledby={`alert-card-title-${alert.id}`}
    >
      <div className="p-5 space-y-3.5">
        {/* Top Badges & Status */}
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-1.5">
            <SeverityBadge severity={alert.severity} size="sm" />
            <span className="text-[10px] font-mono uppercase tracking-wider font-semibold px-2 py-0.5 rounded-md bg-slate-100 text-slate-700">
              {alert.urgency}
            </span>
          </div>

          <div className="flex items-center gap-1.5 text-xs font-mono">
            {alert.isStale && (
              <span className="text-[10px] text-amber-700 bg-amber-50 px-2 py-0.5 rounded font-semibold flex items-center gap-1 border border-amber-200">
                <AlertTriangle className="w-3 h-3 text-amber-600" />
                <span>STALE</span>
              </span>
            )}
            {!active ? (
              <span className="text-[10px] text-slate-500 bg-slate-100 px-2 py-0.5 rounded font-semibold uppercase">
                EXPIRED
              </span>
            ) : (
              <span className="text-[10px] text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded font-bold border border-emerald-200/80 flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                <span>ACTIVE</span>
              </span>
            )}
          </div>
        </div>

        {/* Headline / Title */}
        <h3 id={`alert-card-title-${alert.id}`} className="font-bold text-base sm:text-lg text-navy-950 leading-snug group">
          <Link
            to={`/alert/${encodeURIComponent(alert.id)}`}
            className="hover:text-blue-600 transition-colors inline-block"
          >
            {alert.event}
          </Link>
        </h3>

        {/* Affected Counties / Areas */}
        <div className="flex items-start gap-2 text-xs text-slate-600 bg-slate-50/80 p-2.5 rounded-xl border border-slate-100">
          <MapPin className="w-4 h-4 text-slate-400 flex-shrink-0 mt-0.5" />
          <p className="line-clamp-2 leading-relaxed">
            <strong className="text-navy-900 font-semibold">Zones:</strong> {alert.areaDesc}
          </p>
        </div>

        {/* Issuing Office */}
        <div className="flex items-center gap-1.5 text-xs text-slate-500 font-mono">
          <Building2 className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
          <span className="truncate">{alert.senderName}</span>
        </div>

        {/* Snippet */}
        {alert.headline && alert.headline !== alert.event && (
          <p className="text-xs text-slate-600 line-clamp-2 leading-relaxed italic border-l-2 border-slate-200 pl-2">
            "{alert.headline}"
          </p>
        )}
      </div>

      {/* Card Footer */}
      <div className="px-5 py-3 bg-slate-50/80 border-t border-slate-100 flex flex-wrap items-center justify-between gap-2 text-xs text-slate-500">
        <div className="flex items-center gap-1.5 font-mono text-[11px]">
          <Clock className="w-3.5 h-3.5 text-slate-400" />
          <span>
            {active ? (
              alert.expires ? `Expires ${formatRelativeTime(alert.expires)}` : 'Active until canceled'
            ) : (
              `Expired ${formatRelativeTime(alert.expires)}`
            )}
          </span>
        </div>

        <div className="flex items-center gap-3">
          <a
            href={alert.sourceUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-slate-400 hover:text-blue-600 transition-colors inline-flex items-center gap-1 text-[11px]"
            title="View original National Weather Service source bulletin"
          >
            <span>weather.gov</span>
            <ExternalLink className="w-3 h-3" />
          </a>

          <Link
            to={`/alert/${encodeURIComponent(alert.id)}`}
            className="inline-flex items-center gap-1 font-bold text-xs text-blue-600 hover:text-blue-800 transition-colors"
          >
            <span>Details</span>
            <ChevronRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </article>
  );
};
